export const STUDIO_POLL_MS = 30_000;
export const STUDIO_MAX_AGE_MS = 180_000;

export interface StudioSnapshot {
  present_user_ids: number[];
  valid_for_seconds: number;
  source_fresh: boolean;
}

export function readStudioSnapshot(value: unknown): StudioSnapshot {
  const data = (value as { data?: Partial<StudioSnapshot> } | null)?.data;
  if (!data || !Array.isArray(data.present_user_ids)
    || !data.present_user_ids.every(id => Number.isSafeInteger(id) && id > 0)
    || typeof data.source_fresh !== 'boolean'
    || typeof data.valid_for_seconds !== 'number' || !Number.isFinite(data.valid_for_seconds)) {
    throw new Error('Invalid studio presence response');
  }
  return data as StudioSnapshot;
}

// One poller per authenticated page, never one request per avatar.
// Unknown/stale attendance deliberately produces no claim of studio presence.
export function watchStudioPresence(
  publish: (ids: ReadonlySet<number>) => void,
  request: (signal: AbortSignal) => Promise<StudioSnapshot>,
  page: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'> = document,
) {
  let stopped = false;
  let busy = false;
  let poll: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let published = '';
  const update = (ids: number[]) => {
    const sorted = [...new Set(ids)].sort((a, b) => a - b);
    const signature = sorted.join(',');
    if (signature !== published) {
      published = signature;
      publish(new Set(sorted));
    }
  };
  const refresh = async () => {
    if (stopped || busy) return;
    clearTimeout(poll);
    if (page.hidden) return;
    busy = true;
    controller = new AbortController();
    const started = Date.now();
    const deadline = setTimeout(() => controller?.abort(), 10_000);
    try {
      const snapshot = await request(controller.signal);
      if (stopped) return;
      clearTimeout(expiry);
      const life = Math.min(STUDIO_MAX_AGE_MS, snapshot.valid_for_seconds * 1000) - (Date.now() - started);
      if (!snapshot.source_fresh || life <= 0) update([]);
      else {
        update(snapshot.present_user_ids);
        expiry = setTimeout(() => update([]), life);
      }
    } catch {
      if (!stopped) { clearTimeout(expiry); update([]); }
    } finally {
      clearTimeout(deadline);
      busy = false;
      if (!stopped) poll = setTimeout(() => void refresh(), STUDIO_POLL_MS);
    }
  };
  const visible = () => { if (!page.hidden) void refresh(); };
  page.addEventListener('visibilitychange', visible);
  void refresh();
  return () => {
    stopped = true;
    clearTimeout(poll);
    clearTimeout(expiry);
    controller?.abort();
    page.removeEventListener('visibilitychange', visible);
  };
}
